// Bench app: loads the engine, runs the frame loop, wires controls and
// pointer interaction to bench actions.

import { startCapture } from "./capture";
import { DMM, Geometry, SUPPLY, endpointLabel, holeRightOf, sameEndpoint, type KnobName, type Point } from "./geometry";
import { BAND_COLORS, drawScene, drawScenery, el, makeLayers, triggerView, updateLive, type Layers, type ProbePositions } from "./render";
import type { Action, ActivityView, Catalog, Endpoint, Layout, MeterJack, MeterMode, PartSpec, TrayItem, View } from "./types";
import { runSelfTest } from "./selftest";
import { RemoteSession, account, type Me, type Progress } from "./remote";
import { Engine } from "./wasm";

/** Element by id, for code outside the constructor. */
function byId<T extends HTMLElement = HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

const E12 = [1, 1.2, 1.5, 1.8, 2.2, 2.7, 3.3, 3.9, 4.7, 5.6, 6.8, 8.2];
const DT = 1 / 60;

type Tool =
  | { kind: "place"; spec: PartSpec; spans: number[] }
  | { kind: "wire"; color: string; first: Endpoint | null }
  | { kind: "scopeProbe"; lead: "red" | "black" };

type Drag =
  | { kind: "probe"; lead: "red" | "black"; pointerId: number }
  | { kind: "knob"; knob: KnobName; pointerId: number; startY: number; startValue: number }
  | { kind: "part"; id: string; pointerId: number; start: Point; moved: boolean };

class App {
  private handle: number;
  private layout: Layout;
  private geo: Geometry;
  private layers: Layers;
  private view: View;
  private activity: ActivityView | null = null;
  private activityStamp = "";
  private catalog: Catalog;
  private tool: Tool | null = null;
  private drag: Drag | null = null;
  private selected: string | null = null;
  private probes: ProbePositions = { red: { ...DMM.park.red }, black: { ...DMM.park.black } };
  private sceneStamp = "";
  private hotHole: SVGElement | null = null;
  /** Placement and setting changes are allowed on the free bench and in activities with a tray. */
  private canPlace = true;
  private guideHoles: SVGElement[] = [];
  private holesByRow = new Map<string, SVGElement[]>();
  private holesByCol = new Map<string, SVGElement[]>();
  private readonly t0 = performance.now();
  private eventCount = 0;

  private remote: RemoteSession | null = null;
  private readonly seed: string;
  private readonly activityId: string | null;
  private readonly startedAt = new Date().toISOString();
  private uploaded = false;

  constructor(private readonly engine: Engine, private readonly svg: SVGSVGElement, remote: RemoteSession | null) {
    const q = new URLSearchParams(location.search);
    this.seed = q.get("seed") ?? `practice-${Date.now()}`;
    const wobble = (q.get("wobble") ?? "light") as "off" | "light" | "full";
    const tolerances = q.get("tolerances") !== "off";
    this.catalog = engine.catalog();
    this.activityId = q.get("activity");
    this.remote = remote;
    // A local session always exists: it provides the layout, and the bench
    // state in certificate mode is mirrored from the server's frames.
    if (this.activityId && !remote) {
      this.handle = engine.newActivity(this.activityId, this.seed, 0);
    } else {
      this.handle = engine.newSession({ seed: this.seed, realism: { wobble, tolerances, powerCurves: true }, keyShowsValue: true });
    }
    this.layout = engine.layout(this.handle);
    this.geo = new Geometry(this.layout);
    this.layers = makeLayers(svg);
    drawScenery(this.layers.scenery, this.layout, this.geo);
    const first = remote ? remote.frame() : engine.view(this.handle);
    this.view = first.bench;
    this.activity = first.activity;
    this.bindControls();
    this.bindActivityControls(this.activityId);
    this.bindPointer();
    startCapture(document.body, (c, at) => (this.remote ? this.remote.record(c, at) : engine.record(this.handle, c, at)), () => this.now(), svg);
    this.status(remote
      ? `Certificate attempt: ${this.activity?.title ?? ""}. Hints are off; your actions are graded by the server.`
      : this.activityId
        ? `Activity "${this.activity?.title ?? this.activityId}" started. Seed "${this.seed}".`
        : `Free bench. Seed "${this.seed}", wobble ${wobble}, tolerances ${tolerances ? "on" : "off"}.`);
    this.applyActivityRestrictions();
    if (remote) window.setInterval(() => void remote.poll(), 250);
    void this.renderAccount();
    requestAnimationFrame(() => this.frame());
    if (new URLSearchParams(location.search).get("debug")) {
      (window as unknown as { __sbl: unknown }).__sbl = { view: () => this.view, activity: () => this.activity, apply: (a: Action) => this.engine.apply(this.handle, a, this.now()), log: () => this.engine.log(this.handle) };
    }
    if (new URLSearchParams(location.search).get("selftest")) {
      runSelfTest({
        svg,
        holeScene: (id) => this.geo.hole(id),
        terminalScene: (i, t) => this.geo.terminal(i, t),
        view: () => this.view,
        activity: () => this.activity,
        apply: (a) => this.engine.apply(this.handle, a as Action, this.now()),
        frames: (n) => new Promise((resolve) => { const tick = () => (n-- <= 0 ? resolve() : requestAnimationFrame(tick)); requestAnimationFrame(tick); }),
      });
    }
  }

  private now(): number {
    return performance.now() - this.t0;
  }

  // -- frame loop ------------------------------------------------------------

  private frame(): void {
    const f = this.remote ? this.remote.frame() : this.engine.step(this.handle, DT);
    this.view = f.bench;
    this.activity = f.activity;
    this.render();
    this.renderActivity();
    if (!this.remote && this.activity?.complete && !this.uploaded) void this.uploadPractice();
    requestAnimationFrame(() => this.frame());
  }

  /** Send a finished practice run to the server (for readiness and baselines), if signed in. */
  private async uploadPractice(): Promise<void> {
    if (!this.activity || !this.activityId) return;
    this.uploaded = true;
    try {
      await account.uploadPractice({
        activityId: this.activityId,
        seed: this.seed,
        startedAt: this.startedAt,
        score: this.activity.score,
        maxScore: this.activity.maxScore,
        complete: this.activity.complete,
        passed: this.activity.passed,
        log: this.engine.log(this.handle),
      });
      void this.renderAccount();
    } catch {
      /* not signed in or offline: practice stays local */
    }
  }

  // -- account and progress --------------------------------------------------

  private async renderAccount(): Promise<void> {
    const box = document.getElementById("account");
    if (!box) return;
    box.replaceChildren();
    let me: Awaited<ReturnType<typeof account.me>> | null = null;
    try { me = await account.me(); } catch { me = null; }
    if (!me) {
      box.textContent = "";
      return;
    }
    if (!me.user) {
      let providers = { google: false, microsoft: false, dev: false };
      try { providers = await account.providers(); } catch { /* server absent */ }
      const label = document.createElement("span");
      label.textContent = "Sign in to keep your progress and earn certificates: ";
      box.append(label);
      const returnTo = encodeURIComponent(location.pathname + location.search);
      if (providers.google) { const a = document.createElement("a"); a.href = `/auth/google/start?return_to=${returnTo}`; a.textContent = "Continue with Google"; a.className = "button"; box.append(a, " "); }
      if (providers.microsoft) { const a = document.createElement("a"); a.href = `/auth/microsoft/start?return_to=${returnTo}`; a.textContent = "Continue with Microsoft"; a.className = "button"; box.append(a, " "); }
      if (providers.dev) {
        const b = document.createElement("button"); b.type = "button"; b.id = "dev-login"; b.textContent = "Dev sign-in";
        b.onclick = async () => { await account.devLogin("dev@example.edu", "Dev Student"); location.reload(); };
        box.append(b);
      }
      if (!providers.google && !providers.microsoft && !providers.dev) box.append("(no sign-in provider configured)");
      return;
    }
    const who = document.createElement("span");
    who.id = "who";
    who.textContent = `${me.user.displayName || me.user.email} `;
    const out = document.createElement("button");
    out.type = "button"; out.textContent = "Sign out";
    out.onclick = async () => { await account.logout(); location.reload(); };
    const prof = document.createElement("a");
    prof.href = "/professor.html";
    prof.textContent = me.user.role === "professor" || me.user.role === "admin" ? "Professor dashboard" : "Professors";
    prof.className = "button";
    box.append(who, out, " ", prof);
    void this.renderProgress();
    void this.renderKeys(me);
  }

  /** Account box: notifications, display name, linked class keys (spec 11.1). */
  private async renderKeys(me: Me): Promise<void> {
    const box = document.getElementById("keys");
    if (!box || !me.user) return;
    box.replaceChildren();
    const notes = me.notifications ?? [];
    if (notes.length) {
      const ul = document.createElement("ul");
      ul.className = "notifications";
      ul.id = "notifications";
      for (const n of notes) { const li = document.createElement("li"); li.textContent = n.text; ul.append(li); }
      const dismiss = document.createElement("button");
      dismiss.type = "button"; dismiss.textContent = "Dismiss";
      dismiss.onclick = async () => { await account.readNotifications(); ul.remove(); dismiss.remove(); };
      box.append(ul, dismiss);
    }
    // Display name (shown on certificates).
    const nameRow = document.createElement("div"); nameRow.className = "row";
    const nameLabel = document.createElement("label"); nameLabel.textContent = "Name on certificates ";
    const name = document.createElement("input"); name.type = "text"; name.value = me.user.displayName; name.id = "display-name"; name.maxLength = 80;
    nameLabel.append(name);
    const save = document.createElement("button"); save.type = "button"; save.textContent = "Save";
    save.onclick = async () => {
      try { await account.updateMe(name.value); this.status("Name saved."); void this.renderAccount(); } catch (e) { this.status((e as Error).message, true); }
    };
    nameRow.append(nameLabel, save);
    box.append(nameRow);
    // Linked keys.
    const keys = me.keys ?? [];
    const head = document.createElement("p"); head.textContent = keys.length ? "Your session data is shared with:" : "No class key entered. Your professor can give you one.";
    box.append(head);
    for (const k of keys) {
      const row = document.createElement("div"); row.className = "key-row";
      const txt = document.createElement("span");
      txt.textContent = `${k.professor}, ${k.institution} (verified), for ${k.label} `;
      const code = document.createElement("code"); code.className = "keycode"; code.textContent = k.code;
      const leave = document.createElement("button"); leave.type = "button"; leave.textContent = "Leave";
      leave.onclick = async () => {
        if (!confirm(`Stop sharing your session data with ${k.professor} for ${k.label}?`)) return;
        await account.leaveKey(k.code); void this.renderAccount();
      };
      row.append(txt, code, leave);
      box.append(row);
    }
    // Enter a key: look it up, show the confirmation, then join.
    const form = document.createElement("form"); form.className = "row";
    const input = document.createElement("input"); input.type = "text"; input.placeholder = "Class key, e.g. 7KQ2-M4XD"; input.id = "key-code"; input.setAttribute("aria-label", "Class key");
    const look = document.createElement("button"); look.type = "submit"; look.textContent = "Look up";
    form.append(input, look);
    const confirmBox = document.createElement("div"); confirmBox.className = "confirm"; confirmBox.id = "key-confirm"; confirmBox.hidden = true;
    form.onsubmit = async (e) => {
      e.preventDefault();
      confirmBox.replaceChildren(); confirmBox.hidden = false;
      try {
        const k = await account.lookupKey(input.value.trim());
        const p = document.createElement("p"); p.textContent = k.open ? k.confirmation : `${k.label} (${k.professor}, ${k.institution}): ${k.problem}`;
        confirmBox.append(p);
        if (k.open) {
          const yes = document.createElement("button"); yes.type = "button"; yes.id = "key-join"; yes.textContent = "Share and join";
          yes.onclick = async () => {
            const r = await account.joinKey(k.code);
            if (r.ok) { this.status(`Joined ${k.label}.`); void this.renderAccount(); } else this.status(r.error ?? "could not join", true);
          };
          const no = document.createElement("button"); no.type = "button"; no.textContent = "Cancel";
          no.onclick = () => { confirmBox.hidden = true; };
          confirmBox.append(yes, " ", no);
        }
      } catch (err) {
        const p = document.createElement("p"); p.textContent = (err as Error).message; confirmBox.append(p);
      }
    };
    box.append(form, confirmBox);
  }

  private async renderProgress(): Promise<void> {
    const box = document.getElementById("progress");
    if (!box) return;
    let p: Progress;
    try { p = await account.progress(); } catch { return; }
    box.replaceChildren();
    for (const u of p.units) {
      const h = document.createElement("h3");
      h.textContent = `Unit ${u.unit}: ${u.name}`;
      box.append(h);
      for (const l of u.levels) {
        const row = document.createElement("div");
        row.className = "level-row";
        const done = l.practised.filter(Boolean).length;
        const name = document.createElement("span");
        name.textContent = `${l.name}: ${done}/${l.practised.length} practised`;
        row.append(name, " ");
        if (l.certificate) {
          const a = document.createElement("a");
          a.href = `/c/${l.certificate.code}`;
          a.textContent = `Certificate ${l.certificate.code}`;
          row.append(a);
        } else if (!l.unlocked) {
          const s = document.createElement("span"); s.className = "muted"; s.textContent = "locked until the previous level's certificate"; row.append(s);
        } else {
          const b = document.createElement("button");
          b.type = "button";
          b.className = "cert-start";
          b.dataset.unit = String(u.unit);
          b.dataset.level = String(l.level);
          b.textContent = l.ready ? "Attempt certificate (ready)" : "Attempt certificate";
          b.title = l.ready ? "You have passed every activity of this level in practice." : "Practise every activity of this level first for the best chance.";
          b.onclick = () => {
            const q = new URLSearchParams();
            q.set("certificate", `${u.unit}-${l.level}`);
            location.search = q.toString();
          };
          row.append(b);
        }
        box.append(row);
      }
    }
  }

  // -- activities ------------------------------------------------------------

  private bindActivityControls(current: string | null): void {
    const pick = document.getElementById("activity-pick") as HTMLSelectElement;
    for (const u of this.catalog.curriculum.units) {
      for (const l of u.levels) {
        const group = document.createElement("optgroup");
        group.label = `Unit ${u.unit}: ${l.name}`;
        for (const id of l.activities) {
          const a = this.catalog.activities.find((x) => x.id === id);
          const o = document.createElement("option");
          o.value = id;
          o.textContent = `${a?.title ?? id} (${a?.type ?? ""})`;
          if (id === current) o.selected = true;
          group.append(o);
        }
        pick.append(group);
      }
    }
    (document.getElementById("activity-start") as HTMLButtonElement).onclick = () => this.startActivity(pick.value || null);
    byId<HTMLSelectElement>("scope-readout").onchange = () => this.apply({ type: "setScope", readout: byId<HTMLSelectElement>("scope-readout").value as "peakToPeak" | "rmsAc" | "frequency" });
    byId<HTMLButtonElement>("scope-auto").onclick = () => this.autoScope();
    byId<HTMLButtonElement>("scope-record").onclick = async () => {
      const r = this.remote ? await this.remote.recordScope() : this.engine.recordScope(this.handle, this.now());
      this.status(r.message, r.status !== "ok");
      this.activityStamp = "";
    };
    (document.getElementById("record-reading") as HTMLButtonElement).onclick = async () => {
      const r = this.remote ? await this.remote.recordReading() : this.engine.recordReading(this.handle, this.now());
      this.status(r.message, r.status !== "ok");
      this.activityStamp = "";
    };
  }

  private applyActivityRestrictions(): void {
    const a = this.activity;
    const show = (id: string, on: boolean) => { const n = document.getElementById(id); if (n) n.hidden = !on; };
    // Scope activities use the oscilloscope in place of the DMM.
    const scopeOnly = a?.instrument === "scope";
    document.body.classList.toggle("scope-activity", scopeOnly);
    show("record-reading", !!a && !scopeOnly);
    if (scopeOnly) byId<HTMLDetailsElement>("scope-box").open = true;
    const allowed = (s: string) => !a || a.sources.includes(s);
    show("board-box", allowed("board"));
    this.layers.scenery.querySelector(".supply")?.classList.toggle("disabled", !allowed("psu"));
    this.layers.scenery.querySelector(".battery")?.classList.toggle("disabled", !allowed("bat"));
    this.layers.scenery.querySelector(".board")?.classList.toggle("disabled", !allowed("board"));
    // Tray.
    const tray: TrayItem[] = a?.tray ?? [];
    const has = (k: string) => tray.length === 0 || tray.some((t) => t.kind === k);
    const resSel = document.getElementById("res-value") as HTMLSelectElement;
    const resItem = tray.find((t) => t.kind === "resistor");
    if (resItem && resItem.kind === "resistor") {
      resSel.replaceChildren();
      for (const v of resItem.values) {
        const o = document.createElement("option");
        o.value = String(v);
        o.textContent = v >= 1e6 ? `${v / 1e6} MΩ` : v >= 1e3 ? `${v / 1e3} kΩ` : `${v} Ω`;
        resSel.append(o);
      }
      const tol = document.getElementById("res-tol") as HTMLSelectElement;
      tol.value = String(resItem.tolerance);
      tol.disabled = true;
    }
    const ledItem = tray.find((t) => t.kind === "led");
    if (ledItem && ledItem.kind === "led") {
      const sel = document.getElementById("led-color") as HTMLSelectElement;
      sel.replaceChildren();
      for (const c of ledItem.colors) {
        const o = document.createElement("option");
        o.value = c; o.textContent = c;
        sel.append(o);
      }
    }
    const kindSel = document.getElementById("part-kind") as HTMLSelectElement;
    for (const o of kindSel.options) o.hidden = !has(o.value);
    if (kindSel.selectedOptions[0]?.hidden) {
      const first = [...kindSel.options].find((o) => !o.hidden);
      if (first) { kindSel.value = first.value; this.showPartView(first.value); }
    }
    this.canPlace = tray.length > 0 || !a;
    show("part-picker", this.canPlace);
    show("tool-cancel", this.canPlace);
  }

  /** Reload the page on an activity (or the free bench) with a fresh seed. */
  private startActivity(id: string | null): void {
    const q = new URLSearchParams(location.search);
    if (id) q.set("activity", id); else q.delete("activity");
    q.set("seed", `${id || "bench"}-${Math.random().toString(36).slice(2, 8)}`);
    location.search = q.toString();
  }

  /** The activity after `id` in curriculum order, if any. */
  private nextActivity(id: string): { id: string; title: string } | null {
    const order = this.catalog.curriculum.units.flatMap((u) => u.levels.flatMap((l) => l.activities));
    const next = order[order.indexOf(id) + 1];
    if (!next) return null;
    return { id: next, title: this.catalog.activities.find((x) => x.id === next)?.title ?? next };
  }

  private renderActivity(): void {
    const a = this.activity;
    const box = document.getElementById("activity")!;
    // Every button lives in the row at the bottom of the pane, before Start.
    const actions = document.getElementById("activity-actions")!;
    if (!a) {
      if (this.activityStamp !== "free") {
        this.activityStamp = "free";
        actions.replaceChildren();
        box.replaceChildren();
        const p = document.createElement("p");
        p.className = "muted";
        p.textContent = "Free bench: build anything. Pick an activity above to practise with goals and hints.";
        box.append(p);
      }
      return;
    }
    const stamp = JSON.stringify([a.steps, a.goals, a.score, a.complete, a.lastHint, a.lastRecord, a.activeFindings]);
    if (stamp === this.activityStamp) return;
    const focused = (document.activeElement as HTMLElement | null)?.id;
    this.activityStamp = stamp;
    box.replaceChildren();
    actions.replaceChildren();
    const h = document.createElement("h2");
    const badge = document.createElement("span");
    badge.className = "badge";
    badge.textContent = `Unit ${a.unit} · Level ${["I", "II", "III"][a.level - 1] ?? a.level} · ${a.type}`;
    h.append(badge, a.title);
    box.append(h);
    const intro = document.createElement("p");
    intro.className = "intro";
    intro.textContent = a.intro;
    box.append(intro);
    if (a.type === "troubleshoot" && a.faultCount > 1) {
      const p = document.createElement("p");
      p.textContent = `There are ${a.faultCount} faults.`;
      box.append(p);
    }
    if (a.complete) {
      const c = document.createElement("div");
      c.className = "complete";
      c.textContent = a.passed ? `Complete. Score ${a.score} / ${a.maxScore}.` : `All required goals done, but the score ${a.score} / ${a.maxScore} is below the pass mark. Keep going.`;
      box.append(c);
      if (a.passed && !this.remote) {
        const next = this.nextActivity(a.id);
        if (next) {
          const b = document.createElement("button");
          b.type = "button";
          b.id = "next-activity";
          b.textContent = `Next: ${next.title}`;
          b.onclick = () => this.startActivity(next.id);
          actions.append(b);
        } else {
          const p = document.createElement("p");
          p.className = "muted";
          p.textContent = "That was the last activity for now.";
          box.append(p);
        }
        const again = document.createElement("button");
        again.type = "button";
        again.id = "again-activity";
        again.textContent = "Do it again";
        again.onclick = () => this.startActivity(a.id);
        actions.append(again);
      }
    }
    // Steps.
    if (a.steps.length && a.guidance !== "none") {
      if (a.guidance === "stepByStep") {
        const ol = document.createElement("ol");
        ol.className = "steps";
        a.steps.forEach((s, i) => {
          const li = document.createElement("li");
          li.textContent = s.text;
          li.className = s.done ? "done" : i === a.currentStep ? "current" : "";
          ol.append(li);
        });
        box.append(ol);
      } else {
        const b = document.createElement("button");
        b.type = "button";
        b.id = "what-next";
        b.textContent = "What next?";
        const out = document.createElement("p");
        out.className = "hint";
        out.hidden = true;
        b.onclick = () => {
          out.hidden = false;
          out.textContent = a.currentStep === null ? "All steps are done." : a.steps[a.currentStep].text;
        };
        box.append(out);
        actions.append(b);
      }
    }
    // Goals.
    const ul = document.createElement("ul");
    ul.className = "goals";
    for (const g of a.goals) {
      const li = document.createElement("li");
      li.className = g.achieved ? "achieved" : "todo";
      li.append(g.text);
      if (!g.required) {
        const o = document.createElement("span");
        o.className = "optional";
        o.textContent = " (extra credit)";
        li.append(o);
      }
      if (g.kind === "measurement" && g.recorded) li.append(` — recorded ${g.recorded}`);
      if (g.kind === "prediction") {
        const form = document.createElement("span");
        form.className = "predict";
        const input = document.createElement("input");
        input.type = "number";
        input.step = "any";
        input.id = `predict-${g.id}`;
        input.setAttribute("aria-label", `Prediction for: ${g.text}`);
        if (g.predicted !== null) input.value = String(g.predicted);
        input.disabled = g.achieved;
        const unit = document.createElement("span");
        unit.textContent = ` ${g.unit ?? ""} `;
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = g.achieved ? "Correct" : "Submit";
        b.disabled = g.achieved;
        b.onclick = async () => {
          const v = Number(input.value);
          if (!isFinite(v) || input.value === "") { this.status("Type a number first.", true); return; }
          const r = this.remote ? await this.remote.predict(g.id, v) : this.engine.predict(this.handle, g.id, v, this.now());
          if (!r.ok) this.status(r.error ?? "Could not submit.", true);
          else this.status(r.correct ? "Prediction accepted." : "Not within range. Check your working and try again.", !r.correct);
          this.activityStamp = "";
        };
        form.append(document.createElement("br"), input, unit, b);
        li.append(form);
      }
      ul.append(li);
    }
    box.append(ul);
    const score = document.createElement("p");
    score.className = "score";
    score.textContent = `Score ${a.score} / ${a.maxScore}`;
    box.append(score);
    if (a.lastRecord) {
      const r = document.createElement("div");
      r.className = `result ${a.lastRecord.status === "ok" ? "good" : "bad"}`;
      r.textContent = a.lastRecord.message;
      box.append(r);
    }
    // Certificate mode: finish the stage instead of hints.
    if (this.remote) {
      const info = this.remote.attempt();
      const p = document.createElement("p");
      p.className = "score";
      p.textContent = info ? `Certificate attempt, stage ${info.stage + 1} of ${info.stages}. Hints are off.` : "Certificate attempt.";
      box.append(p);
      const fin = document.createElement("button");
      fin.type = "button";
      fin.id = "finish-stage";
      fin.textContent = info && info.stage + 1 < info.stages ? "Finish this stage" : "Finish and grade";
      fin.disabled = !a.complete;
      fin.onclick = async () => {
        const r = await this.remote!.next();
        if (r.status === "passed" && r.url) {
          this.status(`Passed. Certificate ${r.certificate}.`);
          location.href = `/c/${r.certificate}`;
        } else if (r.status === "failed") {
          this.status(`Not passed this time: score ${r.score} / ${r.maxScore}. Practise and try again.`, true);
          window.setTimeout(() => { location.search = ""; }, 4000);
        } else if (r.status === "inProgress") {
          this.sceneStamp = ""; this.activityStamp = "";
          this.applyActivityRestrictions();
          this.status("Next stage.");
        } else {
          this.status(r.error ?? "Could not finish the stage.", true);
        }
      };
      actions.append(fin);
      const ab = document.createElement("button");
      ab.type = "button";
      ab.textContent = "Abandon attempt";
      ab.onclick = async () => { await this.remote!.abandon(); location.search = ""; };
      actions.append(ab);
      if (focused) document.getElementById(focused)?.focus();
      return;
    }
    // Hints.
    const hb = document.createElement("button");
    hb.type = "button";
    hb.id = "hint-button";
    hb.textContent = "Give me a hint";
    hb.onclick = () => {
      const hint = this.remote ? null : this.engine.hint(this.handle, this.now());
      if (!hint) this.status("Nothing stands out right now. Check the goals list and the circuit.");
      else this.status("");
      this.activityStamp = "";
    };
    actions.append(hb);
    if (a.lastHint) {
      const hint = document.createElement("div");
      hint.className = "hint";
      hint.id = "hint-text";
      hint.textContent = a.lastHint.text;
      box.append(hint);
    }
    if (focused) document.getElementById(focused)?.focus();
  }

  private render(): void {
    const v = this.view;
    const scopeMode = this.activity?.instrument === "scope";
    const stamp = `${v.logLen}|${this.selected}|${this.probes.red.x},${this.probes.red.y}|${this.probes.black.x},${this.probes.black.y}|${v.state.parts.map((p) => p.instance + (v.parts[p.id]?.health ?? "")).join(",")}`;
    // The oscilloscope's trace moves every frame, so scope activities redraw the scene each frame.
    if (stamp !== this.sceneStamp || scopeMode) {
      this.sceneStamp = stamp;
      this.syncProbePositions();
      drawScene(this.layers.scene, v, this.geo, this.probes, this.selected, scopeMode);
      this.renderInspector();
      this.markTakenHoles();
    }
    updateLive(v);
    this.renderScope(v);
    for (const e of v.events) this.pushEvent(e.message, true);
  }

  /**
   * Auto: find the volts and time per division that fit the signal on screen.
   * It first goes to a fast time base, since a slow one thins the trace too much
   * to show the frequency, then fits once the fast trace has filled the screen.
   */
  private autoScope(): void {
    this.apply({ type: "setScope", on: true, timePerDiv: 1e-3 });
    this.status("Auto: finding the signal...");
    window.setTimeout(() => this.fitScope(), 400);
  }

  private fitScope(): void {
    const pts = this.view.trace;
    const m = pts.length > 1 ? traceMeasure(pts) : null;
    if (!m || m.vpp <= 0) {
      this.status("Nothing to fit yet. Connect the probes to a changing voltage, then press Auto again.", true);
      return;
    }
    // Volts: the signal spans about 4 to 6 divisions.
    const volts = [0.1, 0.5, 1, 2, 5, 10].find((x) => m.vpp <= 6 * x) ?? 10;
    // Time: at least two cycles across the 10 divisions, if the frequency is known.
    const times = [1e-5, 1e-4, 1e-3, 1e-2, 1e-1];
    const time = m.freq ? times.find((t) => 10 * t >= 2 / m.freq!) ?? 1e-1 : 1e-3;
    this.apply({ type: "setScope", timePerDiv: time, voltsPerDiv: volts });
    this.status(`Auto: ${fmt(volts, "V")} per division${m.freq ? `, about ${fmt(time * 10, "s")} across the screen` : ""}.`);
  }

  /** Oscilloscope: controls, trace screen, probe labels and readings. */
  private renderScope(v: View): void {
    const sc = v.state.scope;
    const on = byId<HTMLInputElement>("scope-on");
    byId<HTMLSelectElement>("scope-readout").value = sc.readout;
    byId<HTMLButtonElement>("scope-record").hidden = this.activity?.instrument !== "scope";
    const time = byId<HTMLSelectElement>("scope-time");
    const volts = byId<HTMLSelectElement>("scope-volts");
    if (document.activeElement !== time) time.value = String(sc.timePerDiv);
    if (document.activeElement !== volts) volts.value = String(sc.voltsPerDiv);
    on.checked = sc.on;
    const screen = byId("scope-screen") as unknown as SVGSVGElement;
    const W = 400, H = 240, divs = 10, rows = 8;
    const span = sc.timePerDiv * divs;
    const parts: SVGElement[] = [];
    const line = (x1: number, y1: number, x2: number, y2: number, cls: string) => {
      const l = document.createElementNS("http://www.w3.org/2000/svg", "line");
      l.setAttribute("x1", String(x1)); l.setAttribute("y1", String(y1));
      l.setAttribute("x2", String(x2)); l.setAttribute("y2", String(y2));
      l.setAttribute("class", cls);
      return l as unknown as SVGElement;
    };
    for (let i = 0; i <= divs; i++) parts.push(line(i * W / divs, 0, i * W / divs, H, "scope-grid"));
    for (let i = 0; i <= rows; i++) parts.push(line(0, i * H / rows, W, i * H / rows, "scope-grid"));
    parts.push(line(0, H / 2, W, H / 2, "scope-axis"));
    const pts = v.trace;
    if (sc.on && pts.length > 1) {
      const tv = triggerView(pts, sc.timePerDiv, v.time);
      const d = tv.pts.map(([t, volt]) => {
        const x = ((t - tv.t0) / tv.span) * W;
        const y = H / 2 - (volt / sc.voltsPerDiv) * (H / rows);
        return `${x.toFixed(1)},${Math.min(H, Math.max(0, y)).toFixed(1)}`;
      }).join(" ");
      const poly = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
      poly.setAttribute("points", d);
      poly.setAttribute("class", "scope-trace");
      parts.push(poly as unknown as SVGElement);
    }
    screen.replaceChildren(...(parts as unknown as Node[]));
    const probe = (lead: "red" | "black", at: Endpoint | null) => at ? endpointLabel(at) : (lead === "black" ? "reference" : "unplaced");
    byId("scope-probes").textContent = sc.red || sc.black
      ? `Red: ${probe("red", sc.red)} · Black: ${probe("black", sc.black)}`
      : "Place the probes on the bench.";
    const r = sc.on && pts.length > 1 ? traceMeasure(pts) : null;
    byId("scope-readings").textContent = r
      ? `Peak to peak ${fmt(r.vpp, "V")} · RMS ${fmt(r.rmsAc, "V")} (AC) · ${r.freq ? `frequency ${fmt(r.freq, "Hz")}` : "frequency needs two cycles on screen"}`
      : sc.on ? "Waiting for the trace." : "The oscilloscope is off.";
  }

  private syncProbePositions(): void {
    for (const lead of ["red", "black"] as const) {
      const at = this.view.state.meter[lead];
      if (at && !(this.drag?.kind === "probe" && this.drag.lead === lead)) {
        const p = this.geo.endpoint(at);
        if (p) this.probes[lead] = { ...p };
      }
    }
  }

  private markTakenHoles(): void {
    const taken = new Set<string>();
    for (const p of this.view.state.parts) p.holes.forEach((h) => taken.add(h));
    for (const l of this.view.state.leads) {
      if (l.from.kind === "hole") taken.add(l.from.hole);
      if (l.to.kind === "hole") taken.add(l.to.hole);
    }
    for (const n of this.layers.scenery.querySelectorAll<SVGElement>(".hole")) n.classList.toggle("taken", taken.has(n.dataset.hole!));
  }

  // -- actions ---------------------------------------------------------------

  private apply(action: Action): boolean {
    if (this.remote) {
      void this.remote.apply(action).then((r) => {
        if (!r.ok) this.status(r.error ?? "That didn't work.", true);
        else this.status("");
        this.sceneStamp = "";
      }).catch((e: Error) => this.status(e.message, true));
      return true;
    }
    const r = this.engine.apply(this.handle, action, this.now());
    if (!r.ok) this.status(r.error ?? "That didn't work.", true);
    else this.status("");
    return r.ok;
  }

  private status(text: string, error = false): void {
    const s = document.getElementById("status")!;
    s.textContent = text;
    s.classList.toggle("error", error);
  }

  private pushEvent(text: string, warn = false): void {
    const ul = document.getElementById("events")!;
    const li = document.createElement("li");
    li.textContent = `${(this.view.time).toFixed(1)} s: ${text}`;
    if (warn) li.classList.add("warn");
    ul.prepend(li);
    if (++this.eventCount > 50) ul.lastElementChild?.remove();
  }

  // -- controls --------------------------------------------------------------

  private bindControls(): void {
    const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
    const resSel = $<HTMLSelectElement>("res-value");
    for (let dec = 1; dec <= 6; dec++) {
      for (const m of E12) {
        const v = Math.round(m * 10 ** dec * 100) / 100;
        if (v > 1e6) break;
        const o = document.createElement("option");
        o.value = String(v);
        o.textContent = v >= 1e6 ? `${v / 1e6} MΩ` : v >= 1e3 ? `${v / 1e3} kΩ` : `${v} Ω`;
        if (v === 220) o.selected = true;
        resSel.append(o);
      }
    }
    $("place-resistor").onclick = () => this.setTool({ kind: "place", spec: { kind: "resistor", nominal: Number(resSel.value), tolerance: Number($<HTMLSelectElement>("res-tol").value) }, spans: [0, 4] });
    $("place-led").onclick = () => this.setTool({ kind: "place", spec: { kind: "led", color: $<HTMLSelectElement>("led-color").value as PartSpec extends { color: infer C } ? C : never }, spans: [0, 1] });
    $("place-switch").onclick = () => this.setTool({ kind: "place", spec: { kind: "switch" }, spans: [0, 2] });
    $("place-pot").onclick = () => this.setTool({ kind: "place", spec: { kind: "potentiometer", nominal: Number($<HTMLSelectElement>("pot-value").value), tolerance: 0.2 }, spans: [0, 1, 2] });
    $("place-capacitor").onclick = () => this.setTool({ kind: "place", spec: { kind: "capacitor", nominal: Number($<HTMLSelectElement>("cap-value").value), tolerance: 0.2 }, spans: [0, 4] });
    $("place-inductor").onclick = () => this.setTool({ kind: "place", spec: { kind: "inductor", nominal: Number($<HTMLSelectElement>("ind-value").value), tolerance: 0.2 }, spans: [0, 4] });
    $("place-generator").onclick = () => this.setTool({ kind: "place", spec: { kind: "functionGenerator", waveform: $<HTMLSelectElement>("gen-shape").value as "sine" | "square" | "triangle", frequency: Number($<HTMLSelectElement>("gen-freq").value), amplitude: Number($<HTMLSelectElement>("gen-amp").value), offset: 0 }, spans: [0, 4] });
    $("tool-wire").onclick = () => this.setTool({ kind: "wire", color: $<HTMLSelectElement>("wire-color").value, first: null });
    $("tool-cancel").onclick = () => this.setTool(null);
    const kindSel = $<HTMLSelectElement>("part-kind");
    kindSel.onchange = () => {
      if (this.selected) { this.select(null); hideKey(); }
      this.showPartView(kindSel.value);
      this.setTool(null);
    };
    this.showPartView(kindSel.value);

    // Supply front panel: OUTPUT button and knobs live on the drawn supply.
    this.layers.scenery.addEventListener("click", (e) => {
      if ((e.target as Element).closest("#psu-on")) this.toggleOutput();
    });
    $("scope-on").onchange = () => this.apply({ type: "setScope", on: $<HTMLInputElement>("scope-on").checked });
    $("scope-time").onchange = () => this.apply({ type: "setScope", timePerDiv: Number($<HTMLSelectElement>("scope-time").value) });
    $("scope-volts").onchange = () => this.apply({ type: "setScope", voltsPerDiv: Number($<HTMLSelectElement>("scope-volts").value) });
    $("scope-red").onclick = () => { this.setTool({ kind: "scopeProbe", lead: "red" }); this.status("Click a point for the red probe."); };
    $("scope-black").onclick = () => { this.setTool({ kind: "scopeProbe", lead: "black" }); this.status("Click a point for the black probe."); };
    this.layers.scenery.addEventListener("keydown", (e) => {
      const target = e.target as Element;
      if (target.closest("#psu-on") && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); this.toggleOutput(); return; }
      const stop = target.closest<SVGElement>(".dial-stop")?.dataset.mode;
      if (stop && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); this.setMode(stop as MeterMode); return; }
      const knob = target.closest<SVGElement>("[data-knob]")?.dataset.knob as KnobName | undefined;
      if (!knob) return;
      const k = SUPPLY.knobs[knob];
      const step = k.step * (e.shiftKey ? 10 : 1);
      const v = this.knobValue(knob);
      const next = { ArrowUp: v + step, ArrowRight: v + step, ArrowDown: v - step, ArrowLeft: v - step, PageUp: v + k.step * 10, PageDown: v - k.step * 10, Home: 0, End: k.max }[e.key];
      if (next === undefined) return;
      e.preventDefault();
      this.setKnob(knob, next);
    });
    this.layers.scenery.addEventListener("wheel", (e) => {
      const knob = (e.target as Element).closest<SVGElement>("[data-knob]")?.dataset.knob as KnobName | undefined;
      if (!knob) return;
      e.preventDefault();
      const k = SUPPLY.knobs[knob];
      this.setKnob(knob, this.knobValue(knob) - Math.sign(e.deltaY) * k.step * (e.shiftKey ? 10 : 1));
    }, { passive: false });

    const boardOn = $<HTMLInputElement>("board-on");
    boardOn.onchange = () => this.apply({ type: "setBoard", powered: boardOn.checked });
    const boardInput = $<HTMLSelectElement>("board-input");
    for (const [k, label] of Object.entries(this.layout.board.powerInputs)) {
      const o = document.createElement("option");
      o.value = k; o.textContent = label;
      boardInput.append(o);
    }
    boardInput.value = this.view.state.board.powerInput;
    boardInput.onchange = () => this.apply({ type: "setBoard", powerInput: boardInput.value });

    for (const r of document.querySelectorAll<HTMLInputElement>("input[name=dmm-mode]")) r.onchange = () => this.apply({ type: "setMeter", mode: r.value as MeterMode });
    for (const r of document.querySelectorAll<HTMLInputElement>("input[name=dmm-jack]")) r.onchange = () => this.apply({ type: "setMeter", redJack: r.value as MeterJack });
    $("lift-red").onclick = () => this.liftProbe("red");
    $("lift-black").onclick = () => this.liftProbe("black");
    $("replace-fuse").onclick = () => this.apply({ type: "replaceFuse" });
    $("download-log").onclick = () => {
      const blob = new Blob([JSON.stringify(this.engine.log(this.handle))], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "session-log.json";
      a.click();
      URL.revokeObjectURL(a.href);
    };

    // Dial stops (dots) and labels on the meter body turn the dial.
    this.layers.scenery.addEventListener("click", (e) => {
      const t = (e.target as Element).closest<SVGElement>(".dial-label, .dial-stop");
      if (t?.dataset.mode) this.setMode(t.dataset.mode as MeterMode);
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { this.setTool(null); this.cancelDrag(); }
      const active = document.activeElement as HTMLElement | null;
      const partId = active?.dataset?.part;
      const leadId = active?.dataset?.lead;
      if (partId && (e.key === "Delete" || e.key === "Backspace")) { e.preventDefault(); this.apply({ type: "removePart", id: partId }); }
      if (leadId && (e.key === "Delete" || e.key === "Backspace")) { e.preventDefault(); this.apply({ type: "removeLead", id: leadId }); }
      if (partId && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); this.activatePart(partId); }
    });
  }

  private toggleOutput(): void {
    this.apply({ type: "setSupply", on: !this.view.state.supply.on });
  }

  private knobValue(knob: KnobName): number {
    return this.view.state.supply[SUPPLY.knobs[knob].field];
  }

  /// Clamp to the knob's range, snap to its step and apply if it changed.
  private setKnob(knob: KnobName, value: number): void {
    const k = SUPPLY.knobs[knob];
    const snapped = Number((Math.round(Math.min(k.max, Math.max(0, value)) / k.step) * k.step).toFixed(3));
    if (snapped === this.knobValue(knob)) return;
    this.apply({ type: "setSupply", [k.field]: snapped });
  }

  private setMode(mode: MeterMode): void {
    if (this.apply({ type: "setMeter", mode })) {
      const r = document.querySelector<HTMLInputElement>(`input[name=dmm-mode][value=${mode}]`);
      if (r) r.checked = true;
    }
  }

  private liftProbe(lead: "red" | "black"): void {
    this.probes[lead] = { ...DMM.park[lead] };
    this.apply({ type: "probe", lead, at: null });
    this.sceneStamp = "";
  }

  private setTool(tool: Tool | null): void {
    this.tool = tool;
    this.layers.overlay.replaceChildren();
    if (!tool) this.status("");
    else if (tool.kind === "place") this.status("Click a breadboard hole for the first leg.");
    else this.status("Click the first end of the wire.");
  }

  // -- pointer ---------------------------------------------------------------

  private toScene(e: PointerEvent | MouseEvent): Point {
    const ctm = this.svg.getScreenCTM()!;
    const p = this.svg.createSVGPoint();
    p.x = e.clientX; p.y = e.clientY;
    const q = p.matrixTransform(ctm.inverse());
    return { x: q.x, y: q.y };
  }

  private bindPointer(): void {
    this.svg.addEventListener("pointerdown", (e) => this.onDown(e));
    this.svg.addEventListener("pointermove", (e) => this.onMove(e));
    this.svg.addEventListener("pointerup", (e) => this.onUp(e));
    this.svg.addEventListener("pointercancel", () => this.cancelDrag());
    this.svg.addEventListener("focusin", (e) => this.onFocus(e.target as Element));
    this.svg.addEventListener("focusout", () => hideKey());
    this.svg.addEventListener("pointerover", (e) => {
      const part = (e.target as Element).closest<SVGElement>(".part");
      if (part?.dataset.part) this.showKeyFor(part.dataset.part, part);
    });
    this.svg.addEventListener("pointerout", (e) => {
      if ((e.target as Element).closest(".part") && document.activeElement?.closest?.(".part") === null) hideKey();
    });
  }

  private onFocus(target: Element): void {
    const part = target.closest<SVGElement>(".part");
    if (part?.dataset.part) { this.select(part.dataset.part); this.showKeyFor(part.dataset.part, part); }
    const lead = target.closest<SVGElement>(".lead");
    if (lead?.dataset.lead) this.select(lead.dataset.lead);
  }

  private select(id: string | null): void {
    if (this.selected === id) return;
    this.selected = id;
    this.sceneStamp = "";
  }

  private onDown(e: PointerEvent): void {
    const target = e.target as Element;
    const p = this.toScene(e);
    const knob = target.closest<SVGElement>("[data-knob]");
    if (knob?.dataset.knob) {
      const name = knob.dataset.knob as KnobName;
      this.drag = { kind: "knob", knob: name, pointerId: e.pointerId, startY: p.y, startValue: this.knobValue(name) };
      capture(this.svg, e.pointerId);
      knob.focus();
      e.preventDefault();
      return;
    }
    const probeTip = target.closest<SVGElement>("[data-probe]");
    if (probeTip?.dataset.probe && !this.tool) {
      const lead = probeTip.dataset.probe as "red" | "black";
      this.drag = { kind: "probe", lead, pointerId: e.pointerId };
      capture(this.svg, e.pointerId);
      e.preventDefault();
      return;
    }
    const part = target.closest<SVGElement>(".part");
    if (part?.dataset.part && !this.tool) {
      this.drag = { kind: "part", id: part.dataset.part, pointerId: e.pointerId, start: p, moved: false };
      capture(this.svg, e.pointerId);
      part.focus();
      e.preventDefault();
      return;
    }
    const lead = target.closest<SVGElement>(".lead");
    if (lead?.dataset.lead && !this.tool) { this.select(lead.dataset.lead); return; }

    if (this.tool?.kind === "place") {
      const hit = this.geo.nearest(p, 2.2, true);
      if (hit?.kind === "hole") this.placeAt(hit.hole);
      return;
    }
    if (this.tool?.kind === "scopeProbe") {
      const hit = this.geo.nearest(p, 2.6);
      if (hit) {
        this.apply({ type: "scopeProbe", lead: this.tool.lead, at: hit });
        this.setTool(null);
      }
      return;
    }
    if (this.tool?.kind === "wire") {
      const hit = this.geo.nearest(p, 2.6);
      if (!hit) return;
      if (!this.tool.first) {
        this.tool.first = hit;
        this.status(`First end at ${endpointLabel(hit)}. Click the other end.`);
        this.layers.overlay.replaceChildren(el("circle", { cx: this.geo.endpoint(hit)!.x, cy: this.geo.endpoint(hit)!.y, r: 1.6, class: "ghost-mark", fill: "none", stroke: "#1565c0", "stroke-width": 0.4 }));
      } else {
        const color = this.tool.color;
        if (this.apply({ type: "addLead", from: this.tool.first, to: hit, color })) this.setTool({ kind: "wire", color, first: null });
      }
      return;
    }
    // Click on empty space: clear selection.
    if (!target.closest(".part, .lead, [data-probe]")) { this.select(null); hideKey(); }
  }

  private onMove(e: PointerEvent): void {
    const p = this.toScene(e);
    if (this.drag?.kind === "knob") {
      this.setKnob(this.drag.knob, this.drag.startValue + (this.drag.startY - p.y) * SUPPLY.knobs[this.drag.knob].perMm);
      return;
    }
    if (this.drag?.kind === "probe") {
      // The tip snaps to the hole it would land in, so the student sees it.
      const hit = this.highlightNearest(p, false);
      this.probes[this.drag.lead] = hit ? { ...this.geo.endpoint(hit)! } : p;
      this.sceneStamp = "";
      return;
    }
    if (this.drag?.kind === "part") {
      if (Math.hypot(p.x - this.drag.start.x, p.y - this.drag.start.y) > 1.2) this.drag.moved = true;
      if (this.drag.moved) {
        const part = this.view.state.parts.find((q) => q.id === this.drag!.id);
        const hit = this.geo.nearest(p, 3, true);
        this.drawGhost(hit?.kind === "hole" ? hit.hole : null, part ? spansOf(part.holes) : [0, 1]);
      }
      return;
    }
    if (this.tool?.kind === "place") {
      const hit = this.geo.nearest(p, 2.2, true);
      this.drawGhost(hit?.kind === "hole" ? hit.hole : null, this.tool.spans);
    } else if (this.tool?.kind === "wire") {
      this.highlightNearest(p, false);
    }
  }

  private onUp(e: PointerEvent): void {
    const p = this.toScene(e);
    const d = this.drag;
    if (!d) return;
    this.drag = null;
    release(this.svg, e.pointerId);
    if (d.kind === "knob") return;
    this.clearHot();
    this.layers.overlay.replaceChildren();
    if (d.kind === "probe") {
      const hit = this.geo.nearest(p, 2.6);
      const current = this.view.state.meter[d.lead];
      if (hit) {
        if (!sameEndpoint(hit, current)) this.apply({ type: "probe", lead: d.lead, at: hit });
        this.probes[d.lead] = { ...this.geo.endpoint(hit)! };
        this.status(`${d.lead} probe on ${endpointLabel(hit)}.`);
      } else {
        this.probes[d.lead] = p;
        if (current) this.apply({ type: "probe", lead: d.lead, at: null });
      }
      this.sceneStamp = "";
      return;
    }
    if (d.kind === "part") {
      if (!d.moved) { this.activatePart(d.id); return; }
      const part = this.view.state.parts.find((q) => q.id === d.id);
      const hit = this.geo.nearest(p, 3, true);
      if (part && hit?.kind === "hole") {
        const holes = spansOf(part.holes).map((s) => holeRightOf(hit.hole, s));
        if (holes.every((h) => h)) this.apply({ type: "movePart", id: d.id, holes: holes as string[] });
        else this.status("That doesn't fit on the board.", true);
      }
    }
  }

  private cancelDrag(): void {
    if (this.drag?.kind === "probe") this.syncProbePositions();
    this.drag = null;
    this.layers.overlay.replaceChildren();
    this.clearHot();
    this.sceneStamp = "";
  }

  private activatePart(id: string): void {
    const part = this.view.state.parts.find((q) => q.id === id);
    if (!part) return;
    this.select(id);
    if (part.spec.kind === "switch") this.apply({ type: "setSwitch", id, closed: !part.closed });
  }

  private placeAt(hole: string): void {
    if (this.tool?.kind !== "place") return;
    const holes = this.tool.spans.map((s) => holeRightOf(hole, s));
    if (!holes.every((h) => h)) { this.status("That doesn't fit on the board.", true); return; }
    if (this.apply({ type: "placePart", part: this.tool.spec, holes: holes as string[] })) {
      this.status("Placed. Click another hole to place one more, or Cancel.");
    }
  }

  private drawGhost(hole: string | null, spans: number[]): void {
    this.layers.overlay.replaceChildren();
    if (!hole) return;
    const g = el("g", { class: "ghost" });
    const pts = spans.map((s) => holeRightOf(hole, s)).map((h) => (h ? this.geo.hole(h) : undefined));
    if (pts.some((q) => !q)) return;
    const ps = pts as Point[];
    const y = ps[0].y - 3.2;
    for (const q of ps) {
      g.append(el("line", { x1: q.x, y1: q.y, x2: q.x, y2: y }));
      g.append(el("circle", { cx: q.x, cy: q.y, r: 0.9 }));
    }
    g.append(el("line", { x1: ps[0].x, y1: y, x2: ps[ps.length - 1].x, y2: y }));
    this.layers.overlay.append(g);
  }

  /** Highlight the nearest target: its row and column as a crosshair, the
   *  holes it is connected to, and the hole itself. Returns the target. */
  private highlightNearest(p: Point, stripOnly: boolean): Endpoint | null {
    this.clearHot();
    const hit = this.geo.nearest(p, 2.6, stripOnly);
    if (!hit) return null;
    if (hit.kind === "hole") {
      const { row, col } = holeRowCol(hit.hole);
      this.indexHoles();
      for (const n of [...(this.holesByRow.get(row) ?? []), ...(this.holesByCol.get(col) ?? [])]) {
        n.classList.add("guide"); this.guideHoles.push(n);
      }
      const net = this.layout.breadboard.holes.find((h) => h.id === hit.hole)?.net;
      if (net) {
        for (const h of this.layout.breadboard.holes) {
          if (h.net !== net) continue;
          const n = this.holeEl(h.id);
          if (n) { n.classList.add("strip"); this.guideHoles.push(n); }
        }
      }
    }
    const sel = hit.kind === "hole" ? `.hole[data-hole="${hit.hole}"]` : `[data-instrument="${hit.instrument}"][data-terminal="${hit.terminal}"]`;
    const n = this.layers.scenery.querySelector<SVGElement>(sel);
    if (n) { n.classList.add("hot"); this.hotHole = n; }
    return hit;
  }

  private clearHot(): void {
    this.hotHole?.classList.remove("hot");
    this.hotHole = null;
    for (const n of this.guideHoles) n.classList.remove("guide", "strip");
    this.guideHoles = [];
  }

  private holeEl(id: string): SVGElement | null {
    return this.layers.scenery.querySelector<SVGElement>(`.hole[data-hole="${id}"]`);
  }

  private indexHoles(): void {
    if (this.holesByRow.size) return;
    for (const h of this.layout.breadboard.holes) {
      const n = this.holeEl(h.id);
      if (!n) continue;
      const { row, col } = holeRowCol(h.id);
      (this.holesByRow.get(row) ?? this.holesByRow.set(row, []).get(row)!).push(n);
      (this.holesByCol.get(col) ?? this.holesByCol.set(col, []).get(col)!).push(n);
    }
  }

  // -- key and inspector -----------------------------------------------------

  private showKeyFor(id: string, anchor: Element): void {
    const info = this.view.parts[id];
    if (!info?.key) { hideKey(); return; }
    const key = document.getElementById("key")!;
    key.replaceChildren();
    const title = document.createElement("div");
    title.textContent = `${id}: ${info.key}`;
    key.append(title);
    if (info.bands) {
      const sw = document.createElement("div");
      sw.className = "swatches";
      for (const b of info.bands) {
        const s = document.createElement("span");
        s.className = "swatch";
        s.style.background = BAND_COLORS[b];
        s.title = b;
        sw.append(s);
      }
      key.append(sw);
    }
    const r = anchor.getBoundingClientRect();
    key.hidden = false;
    key.style.left = `${Math.min(window.innerWidth - 300, r.left)}px`;
    key.style.top = `${r.bottom + 6}px`;
  }

  /** Show the settings view for a part kind (resistor, led, switch, potentiometer, wire). */
  private showPartView(kind: string): void {
    for (const v of document.querySelectorAll<HTMLElement>("#tray-box .part-view")) v.hidden = v.dataset.kind !== kind;
    const sel = document.getElementById("part-kind") as HTMLSelectElement;
    if (sel.value !== kind) sel.value = kind;
  }

  /** The Parts panel follows the selection: it jumps to the part's kind,
   *  loads its settings, shows its readings and offers Update and Remove. */
  private renderInspector(): void {
    const info = document.getElementById("part-info")!;
    const actions = document.getElementById("part-actions")!;
    const name = document.getElementById("part-selected")!;
    info.replaceChildren();
    for (const n of [...actions.children]) if (!(n as HTMLElement).classList.contains("place-only")) n.remove();
    const id = this.selected;
    const part = id ? this.view.state.parts.find((p) => p.id === id) : undefined;
    const lead = id ? this.view.state.leads.find((l) => l.id === id) : undefined;
    const selected = !!(part || lead);
    for (const n of document.querySelectorAll<HTMLElement>("#tray-box .place-only")) n.hidden = selected;
    if (selected) (document.getElementById("tray-box") as HTMLDetailsElement).open = true;
    if (!selected) {
      name.textContent = "";
      const p = document.createElement("p");
      p.className = "muted";
      p.textContent = this.canPlace ? "Pick a kind and place it, or click a part or wire on the bench to select it." : "Click a part or wire on the bench to select it.";
      info.append(p);
      return;
    }
    const line = (text: string) => { const p = document.createElement("p"); p.textContent = text; info.append(p); };
    const button = (label: string, onClick: () => void, bid?: string) => {
      const b = document.createElement("button"); b.type = "button"; b.textContent = label; b.onclick = onClick;
      if (bid) b.id = bid;
      actions.append(b);
    };
    const deselect = () => { this.select(null); hideKey(); };
    if (lead) {
      if (this.canPlace) this.showPartView("wire");
      name.textContent = `Selected: wire ${lead.id}`;
      const color = document.getElementById("wire-color") as HTMLSelectElement;
      color.value = lead.color || "red";
      line(`${lead.color || "wire"} wire ${lead.id}: ${endpointLabel(lead.from)} to ${endpointLabel(lead.to)}`);
      if (this.canPlace) button("Update colour", () => {
        if (color.value === lead.color) { this.status("No change."); return; }
        this.apply({ type: "setLead", id: lead.id, color: color.value });
      }, "update-part");
      button("Remove", () => this.apply({ type: "removeLead", id: lead.id }), "remove-part");
      button("Deselect", deselect);
      return;
    }
    if (!part) return;
    const spec = part.spec;
    if (this.canPlace) this.showPartView(spec.kind === "functionGenerator" ? "generator" : spec.kind);
    name.textContent = `Selected: ${part.id}`;
    const partInfo = this.view.parts[part.id];
    const res = this.view.solution.components[part.id];
    line(`${part.id}: ${partInfo?.label ?? spec.kind} in ${part.holes.join(", ")}${partInfo?.health === "open" ? " (failed)" : ""}`);
    if (partInfo?.key) line(`Bands: ${partInfo.key}`);
    if (res?.kind === "resistor") line(`${fmt(res.voltage, "V")} across, ${fmt(res.current, "A")}, ${fmt(res.power, "W")} of ${res.rating} W`);
    if (res?.kind === "led") line(`${fmt(res.voltage, "V")} forward, ${fmt(res.current, "A")}${res.overdriven ? " (over rated current!)" : ""}`);
    // Load the part's settings into the view and offer an update.
    let updated: (() => PartSpec | null) | null = null;
    if (spec.kind === "resistor") {
      const value = document.getElementById("res-value") as HTMLSelectElement;
      const tol = document.getElementById("res-tol") as HTMLSelectElement;
      value.value = String(spec.nominal); tol.value = String(spec.tolerance);
      updated = () => (Number(value.value) === spec.nominal && Number(tol.value) === spec.tolerance) ? null : { kind: "resistor", nominal: Number(value.value), tolerance: Number(tol.value) };
    } else if (spec.kind === "led") {
      const color = document.getElementById("led-color") as HTMLSelectElement;
      color.value = spec.color;
      updated = () => color.value === spec.color ? null : { kind: "led", color: color.value as typeof spec.color };
    } else if (spec.kind === "potentiometer") {
      const value = document.getElementById("pot-value") as HTMLSelectElement;
      value.value = String(spec.nominal);
      updated = () => Number(value.value) === spec.nominal ? null : { kind: "potentiometer", nominal: Number(value.value), tolerance: spec.tolerance };
      const label = document.createElement("label");
      label.textContent = "Wiper ";
      const r = document.createElement("input");
      r.type = "range"; r.min = "0"; r.max = "1"; r.step = "0.01"; r.value = String(part.position);
      r.oninput = () => this.apply({ type: "setPot", id: part.id, position: Number(r.value) });
      label.append(r);
      info.append(label);
    }
    if (spec.kind === "capacitor") {
      const sel = document.getElementById("cap-value") as HTMLSelectElement;
      selectValue(sel, spec.nominal, "F");
      updated = () => Number(sel.value) === spec.nominal ? null : { kind: "capacitor", nominal: Number(sel.value), tolerance: spec.tolerance };
    } else if (spec.kind === "inductor") {
      const sel = document.getElementById("ind-value") as HTMLSelectElement;
      selectValue(sel, spec.nominal, "H");
      updated = () => Number(sel.value) === spec.nominal ? null : { kind: "inductor", nominal: Number(sel.value), tolerance: spec.tolerance };
    } else if (spec.kind === "functionGenerator") {
      const shape = document.getElementById("gen-shape") as HTMLSelectElement;
      const freq = document.getElementById("gen-freq") as HTMLSelectElement;
      const amp = document.getElementById("gen-amp") as HTMLSelectElement;
      shape.value = spec.waveform;
      selectValue(freq, spec.frequency, "Hz");
      selectValue(amp, spec.amplitude, "V");
      updated = () => {
        const waveform = shape.value as "sine" | "square" | "triangle";
        const frequency = Number(freq.value), amplitude = Number(amp.value);
        if (waveform === spec.waveform && frequency === spec.frequency && amplitude === spec.amplitude) return null;
        return { kind: "functionGenerator", waveform, frequency, amplitude, offset: spec.offset };
      };
    }
    if (spec.kind === "switch") button(part.closed ? "Open switch" : "Close switch", () => this.apply({ type: "setSwitch", id: part.id, closed: !part.closed }));
    if (updated && this.canPlace) {
      const u = updated;
      button("Update", () => {
        const next = u();
        if (!next) { this.status("No change."); return; }
        this.apply({ type: "setPart", id: part.id, part: next });
      }, "update-part");
    }
    button("Remove", () => this.apply({ type: "removePart", id: part.id }), "remove-part");
    button("Replace with a new one", () => this.apply({ type: "replacePart", id: part.id }));
    button("Deselect", deselect);
  }
}

/** Row and column keys of a hole id: `a5` -> a, 5; `t+2` -> t+, 2. */
function holeRowCol(id: string): { row: string; col: string } {
  const m = /^([a-j]|[tb][+-])(\d+)$/.exec(id);
  return m ? { row: m[1], col: m[2] } : { row: id, col: id };
}

function capture(svg: SVGSVGElement, id: number): void {
  try { svg.setPointerCapture(id); } catch { /* synthetic pointer */ }
}

function release(svg: SVGSVGElement, id: number): void {
  try { svg.releasePointerCapture(id); } catch { /* synthetic pointer */ }
}

function spansOf(holes: string[]): number[] {
  const col = (h: string) => Number(/\d+/.exec(h)?.[0] ?? 0);
  const base = col(holes[0]);
  return holes.map((h) => col(h) - base);
}

/** A value with an SI prefix, as shown in the part pickers, e.g. 100 µF. */
function siLabel(x: number, unit: string): string {
  const prefixes: [number, string][] = [[1, ""], [1e-3, "m"], [1e-6, "µ"], [1e-9, "n"], [1e-12, "p"], [1e3, "k"], [1e6, "M"]];
  const [scale, prefix] = prefixes.find(([s]) => Math.abs(x) >= s * 0.999 && Math.abs(x) < s * 1000) ?? [1, ""];
  return `${Number((x / scale).toPrecision(4))} ${prefix}${unit}`;
}

/** Select the option for `value`, adding one if the list does not offer it. */
function selectValue(sel: HTMLSelectElement, value: number, unit: string): void {
  let match = Array.from(sel.options).find((o) => Number(o.value) === value);
  if (!match) {
    match = document.createElement("option");
    match.value = String(value);
    match.textContent = siLabel(value, unit);
    sel.append(match);
  }
  sel.value = match.value;
}

/** Peak to peak, RMS about the mean, and frequency of a trace (same method as the engine). */
function traceMeasure(pts: [number, number][]): { vpp: number; rmsAc: number; freq: number | null } {
  const vs = pts.map((p) => p[1]);
  const n = vs.length;
  const mean = vs.reduce((a, b) => a + b, 0) / n;
  const vpp = Math.max(...vs) - Math.min(...vs);
  const rmsAc = Math.sqrt(vs.reduce((a, v) => a + (v - mean) ** 2, 0) / n);
  const cross: number[] = [];
  for (let i = 1; i < n; i++) {
    const a = vs[i - 1] - mean, b = vs[i] - mean;
    if (a < 0 && b >= 0) cross.push(pts[i - 1][0] + (-a / (b - a)) * (pts[i][0] - pts[i - 1][0]));
  }
  const freq = cross.length >= 2 ? (cross.length - 1) / (cross[cross.length - 1] - cross[0]) : null;
  return { vpp, rmsAc, freq };
}

function fmt(v: number, unit: string): string {
  const a = Math.abs(v);
  if (!isFinite(v)) return "—";
  if (a >= 1) return `${v.toFixed(3)} ${unit}`;
  if (a >= 1e-3) return `${(v * 1e3).toFixed(2)} m${unit}`;
  if (a >= 1e-6) return `${(v * 1e6).toFixed(1)} µ${unit}`;
  return `0 ${unit}`;
}

function hideKey(): void {
  document.getElementById("key")!.hidden = true;
}

/** Keep the sticky control column inside the viewport: its top edge sits
 *  below the page header until the page scrolls, so size it from where it
 *  actually is rather than from the full viewport height. */
function fitControls(): void {
  const aside = document.querySelector<HTMLElement>(".controls");
  if (!aside) return;
  if (getComputedStyle(aside).position !== "sticky") { aside.style.maxHeight = ""; return; }
  const top = aside.getBoundingClientRect().top;
  aside.style.maxHeight = `${Math.max(120, window.innerHeight - top - 12)}px`;
}

async function main(): Promise<void> {
  const svg = document.getElementById("bench") as unknown as SVGSVGElement;
  // The account pane opens on the first visit in this browser, then starts collapsed.
  try {
    if (!localStorage.getItem("sbl:accountSeen")) {
      (document.getElementById("keys-box") as HTMLDetailsElement).open = true;
      localStorage.setItem("sbl:accountSeen", "1");
    }
  } catch { /* storage unavailable: leave it collapsed */ }
  fitControls();
  window.addEventListener("resize", fitControls);
  window.addEventListener("scroll", fitControls, { passive: true });
  // The header grows when the account line and notice render.
  const header = document.querySelector("header");
  if (header && "ResizeObserver" in window) new ResizeObserver(fitControls).observe(header);
  try {
    const engine = await Engine.load(__ENGINE_WASM__);
    const cert = new URLSearchParams(location.search).get("certificate");
    let remote: RemoteSession | null = null;
    if (cert) {
      const [unit, level] = cert.split("-").map(Number);
      try {
        remote = await RemoteSession.start(unit, level);
      } catch (e) {
        const s = document.getElementById("status")!;
        s.textContent = `Could not start the certificate attempt: ${(e as Error).message}`;
        s.classList.add("error");
      }
    }
    new App(engine, svg, remote);
  } catch (e) {
    const s = document.getElementById("status")!;
    s.textContent = `Could not start the engine: ${(e as Error).message}`;
    s.classList.add("error");
  }
}

main();
